import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { 
  Scale, History, FileCheck, Download, Loader2, CheckSquare,
  CheckCircle2, Clock, User, FileText, Lock, ThumbsUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * CalculationAuditDialog - Manages the audit trail for calculated fields
 * Used for court documentation, settlement negotiations, and record-keeping
 */
export default function CalculationAuditDialog({
  open,
  onOpenChange,
  caseId,
  field,
  currentValue,
  sourceFields,
  token
}) {
  const [auditNotes, setAuditNotes] = useState('');
  const [audits, setAudits] = useState([]);
  const [loadingAudits, setLoadingAudits] = useState(false);
  const [creatingAudit, setCreatingAudit] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [approvalFor, setApprovalFor] = useState('');

  // Fetch audits when dialog opens
  const fetchAudits = useCallback(async () => {
    if (!field) return;
    setLoadingAudits(true);
    try {
      const response = await fetch(
        `${API_URL}/api/case-management/cases/${caseId}/field-audits/${field.tag}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setAudits(data.audits || []);
      }
    } catch (error) {
      console.error('Error fetching audits:', error);
    } finally {
      setLoadingAudits(false);
    }
  }, [caseId, field, token]);

  useEffect(() => {
    if (open && field) {
      fetchAudits();
    }
  }, [open, field, fetchAudits]);

  // Create new audit entry
  const createAuditEntry = async () => {
    if (!field) return;
    setCreatingAudit(true);
    try {
      const calculation = field.calculation || {};
      
      const response = await fetch(
        `${API_URL}/api/case-management/cases/${caseId}/field-audits`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            field_tag: field.tag,
            field_name: field.name,
            calculated_value: currentValue,
            source_fields: sourceFields || [],
            formula_used: calculation.operator || 'sum',
            notes: auditNotes
          })
        }
      );

      if (response.ok) {
        toast.success('Audit entry recorded');
        setAuditNotes('');
        fetchAudits();
      } else {
        toast.error('Failed to create audit entry');
      }
    } catch (error) {
      toast.error('Failed to create audit entry');
    } finally {
      setCreatingAudit(false);
    }
  };

  // Approve an audit entry
  const approveAudit = async () => {
    if (!selectedAudit) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/case-management/cases/${caseId}/field-audits/${selectedAudit.id}/approve`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            approval_for: approvalFor,
            approval_notes: approvalNotes
          })
        }
      );

      if (response.ok) {
        toast.success('Audit entry approved');
        setApprovalDialogOpen(false);
        setSelectedAudit(null);
        setApprovalNotes('');
        setApprovalFor('');
        fetchAudits();
      } else {
        toast.error('Failed to approve audit entry');
      }
    } catch (error) {
      toast.error('Failed to approve audit entry');
    }
  };

  // Export audits as JSON
  const exportAuditsJSON = () => {
    const approvedAudits = audits.filter(a => a.approved);
    if (approvedAudits.length === 0) {
      toast.error('No approved audits to export');
      return;
    }

    const data = {
      field_name: field?.name,
      field_tag: field?.tag,
      export_date: new Date().toISOString(),
      audits: approvedAudits
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${field?.tag}_audit_trail.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Audit trail exported');
  };

  // Export audits as PDF-ready document
  const exportAuditsPDF = async (documentType) => {
    const approvedAudits = audits.filter(a => a.approved);
    if (approvedAudits.length === 0) {
      toast.error('No approved audits to export');
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/case-management/cases/${caseId}/field-audits/export-pdf`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            field_tag: field?.tag,
            document_type: documentType
          })
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${field?.tag}_${documentType}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('PDF exported');
      } else {
        toast.error('Failed to generate PDF');
      }
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString();
  };

  if (!field) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scale className="w-5 h-5 text-purple-600" />
              Calculation Audit Trail
            </DialogTitle>
            <DialogDescription>
              {field.name} - Record calculations for court documentation and settlement negotiations
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Current Value Display */}
            <div className="p-3 bg-slate-100 rounded-lg">
              <div className="text-sm text-muted-foreground">Current Calculated Value:</div>
              <div className="text-2xl font-bold text-purple-700">{currentValue}</div>
              {sourceFields && sourceFields.length > 0 && (
                <div className="text-xs text-muted-foreground mt-1">
                  Source fields: {sourceFields.join(', ')}
                </div>
              )}
            </div>

            {/* Record New Calculation */}
            <div className="p-4 border rounded-lg bg-purple-50">
              <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                <FileCheck className="w-4 h-4 text-purple-600" />
                Record Current Calculation
              </h4>
              <Textarea
                placeholder="Add notes about this calculation (optional)..."
                value={auditNotes}
                onChange={(e) => setAuditNotes(e.target.value)}
                rows={2}
                className="mb-3"
              />
              <Button
                onClick={createAuditEntry}
                disabled={creatingAudit}
                size="sm"
                className="bg-purple-600 hover:bg-purple-700"
              >
                {creatingAudit ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CheckSquare className="w-4 h-4 mr-2" />
                )}
                Record for Audit Trail
              </Button>
            </div>

            {/* Export Buttons */}
            <div className="flex flex-wrap gap-2 justify-end border-t pt-3">
              <span className="text-sm text-muted-foreground mr-auto">Export approved calculations:</span>
              <Button variant="outline" size="sm" onClick={exportAuditsJSON}>
                <Download className="w-4 h-4 mr-2" />
                JSON
              </Button>
              <Select onValueChange={(type) => exportAuditsPDF(type)}>
                <SelectTrigger className="w-[200px] h-8">
                  <SelectValue placeholder="Download PDF..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="settlement_demand">Settlement Demand</SelectItem>
                  <SelectItem value="court_filing">Court Filing Exhibit</SelectItem>
                  <SelectItem value="mediation">Mediation Statement</SelectItem>
                  <SelectItem value="arbitration">Arbitration Exhibit</SelectItem>
                  <SelectItem value="client_presentation">Client Presentation</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Audit History */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <History className="w-4 h-4" />
                Audit History
              </h4>
              
              {loadingAudits ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  <span className="text-sm text-muted-foreground">Loading audits...</span>
                </div>
              ) : audits.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <History className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No audit entries yet</p>
                  <p className="text-xs">Record calculations above to create an audit trail</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {audits.map((audit) => (
                    <div 
                      key={audit.id} 
                      className={`p-3 rounded-lg border ${audit.approved ? 'bg-emerald-50 border-emerald-200' : 'bg-white'}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-lg font-bold">
                              {typeof audit.calculated_value === 'number' 
                                ? `$${audit.calculated_value.toLocaleString()}` 
                                : audit.calculated_value}
                            </span>
                            {audit.approved ? (
                              <Badge className="bg-emerald-500">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Approved
                              </Badge>
                            ) : (
                              <Badge variant="outline">Pending</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDate(audit.created_at)}
                            </span>
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {audit.recorded_by}
                            </span>
                          </div>
                          {audit.notes && (
                            <p className="text-sm text-gray-600 mt-2 p-2 bg-slate-100 rounded">
                              <FileText className="w-3 h-3 inline mr-1" />
                              {audit.notes}
                            </p>
                          )}
                          {audit.approved && audit.approval_for && (
                            <div className="mt-2 text-xs text-emerald-700 flex items-center gap-2">
                              <ThumbsUp className="w-3 h-3" />
                              Approved for: {audit.approval_for.replace(/_/g, ' ')}
                              {audit.approval_notes && ` - "${audit.approval_notes}"`}
                            </div>
                          )}
                        </div>
                        {!audit.approved && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedAudit(audit);
                              setApprovalDialogOpen(true);
                            }}
                            className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                          >
                            <Lock className="w-3 h-3 mr-1" />
                            Approve
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-emerald-600" />
              Approve Calculation
            </DialogTitle>
            <DialogDescription>
              Lock this calculation value for official use
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-slate-100 rounded-lg">
              <div className="text-sm text-muted-foreground">Value to Approve:</div>
              <div className="text-xl font-bold font-mono">
                {typeof selectedAudit?.calculated_value === 'number'
                  ? `$${selectedAudit.calculated_value.toLocaleString()}`
                  : selectedAudit?.calculated_value}
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Approval Purpose</label>
              <Select value={approvalFor} onValueChange={setApprovalFor}>
                <SelectTrigger>
                  <SelectValue placeholder="Select purpose..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="settlement_demand">Settlement Demand</SelectItem>
                  <SelectItem value="court_filing">Court Filing</SelectItem>
                  <SelectItem value="mediation">Mediation</SelectItem>
                  <SelectItem value="arbitration">Arbitration</SelectItem>
                  <SelectItem value="client_presentation">Client Presentation</SelectItem>
                  <SelectItem value="internal_record">Internal Record</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Approval Notes (optional)</label>
              <Textarea
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                placeholder="Any notes about this approval..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApprovalDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={approveAudit}
              disabled={!approvalFor}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Approve & Lock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
